import React, { useState } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import http from "../Utils/axiosConfig";
import ToastService from "../Utils/ToastService";
import DairyAdminsList from "./DairyAdminsList";
import "./RegisterDairy.css";

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

const INIT_DAIRY_FORM = {
  username: "",
  password: "",
  confirmPassword: "",
  en_name: "",
  mr_name: "",
  email: "",
  multi: "1",
  location: "",
  referred_by: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const RegisterDairy = () => {
  const [unlocked, setUnlocked] = useState(false);
  const [secretKey, setSecretKey] = useState("");
  const [secretError, setSecretError] = useState("");
  const [secretLoading, setSecretLoading] = useState(false);

  const [tab, setTab] = useState(0);

  const [formData, setFormData] = useState(INIT_DAIRY_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleSecretSubmit = async (e) => {
    e.preventDefault();
    if (!secretKey) {
      setSecretError("Secret key is required");
      return;
    }
    setSecretLoading(true);
    setSecretError("");
    try {
      const res = await http.post("login/verify-secret", { secretKey });
      if (res.code === "SECRET_VERIFIED") {
        setUnlocked(true);
      } else {
        setSecretError("Incorrect secret key");
      }
    } catch (err) {
      console.error("Verify secret error:", err);
      setSecretError("Internal Server error.");
    } finally {
      setSecretLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.username.trim()) newErrors.username = "Username is required";
    if (!formData.password) newErrors.password = "Password is required";
    else if (formData.password.length < 4)
      newErrors.password = "Password must be at least 4 characters";
    if (!formData.confirmPassword)
      newErrors.confirmPassword = "Please confirm the password";
    else if (formData.password !== formData.confirmPassword)
      newErrors.confirmPassword = "Passwords do not match";
    if (!formData.mr_name.trim())
      newErrors.mr_name = "Marathi name is required";
    if (formData.email.trim() && !EMAIL_RE.test(formData.email.trim()))
      newErrors.email = "Enter a valid email address";
    if (!formData.multi || Number(formData.multi) <= 0)
      newErrors.multi = "Bank multiplier must be greater than 0";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const res = await http.post("login/register-dairy", {
        secretKey,
        username: formData.username,
        password: formData.password,
        en_name: formData.en_name,
        mr_name: formData.mr_name,
        email: formData.email.trim(),
        multi: Number(formData.multi),
        location: formData.location.trim(),
        referred_by: formData.referred_by.trim(),
      });

      if (res.code === "DAIRY_ADMIN_CREATED") {
        ToastService.success(
          `New dairy admin created successfully ✅ (username: ${formData.username})`,
        );
        setFormData(INIT_DAIRY_FORM);
        setErrors({});
      } else if (res.code === "USERNAME_ALREADY_EXISTS") {
        setErrors((prev) => ({
          ...prev,
          username: "This username is already taken",
        }));
      } else if (res.code === "INVALID_EMAIL") {
        setErrors((prev) => ({
          ...prev,
          email: "Enter a valid email address",
        }));
      } else if (res.code === "INVALID_CREDENTIALS") {
        ToastService.error("Secret key is no longer valid, please try again");
        setUnlocked(false);
        setSecretKey("");
      } else {
        ToastService.error(res.message || "Failed to create dairy admin");
      }
    } catch (err) {
      console.error("Register dairy error:", err);
      ToastService.error("Internal Server error.");
    } finally {
      setLoading(false);
    }
  };

  if (!unlocked) {
    return (
      <div className="lgform">
        <form onSubmit={handleSecretSubmit}>
          <div className="login-form">
            <h2>Access Required</h2>
            <label className="label">Secret Key</label>
            <input
              type="password"
              value={secretKey}
              onChange={(e) => {
                setSecretKey(e.target.value);
                setSecretError("");
              }}
            />
            {secretError && <p className="error error-input">{secretError}</p>}
            <button type="submit" disabled={secretLoading}>
              {secretLoading ? "Verifying..." : "Continue"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="rd-container">
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="fullWidth"
        centered
        textColor="inherit"
        TabIndicatorProps={{ style: { backgroundColor: "#ff9800" } }}
        sx={{
          "& .MuiTab-root": { color: "#bbb", fontWeight: "bold", fontSize: "16px" },
          "& .Mui-selected": { color: "#ff9800" },
        }}
      >
        <Tab label="Create New" />
        <Tab label="Dairy Admins" />
      </Tabs>

      <TabPanel value={tab} index={0}>
        <form className="dre-form rd-form" onSubmit={handleSubmit}>
          <div className="dre-field dre-inline">
            <label className="dre-field-label">Username</label>
            <input
              type="text"
              name="username"
              className="dre-input"
              value={formData.username}
              onChange={handleChange}
            />
          </div>
          {errors.username && <p className="error error-input">{errors.username}</p>}

          <div className="dre-field dre-inline">
            <label className="dre-field-label">Password</label>
            <input
              type="password"
              name="password"
              className="dre-input"
              value={formData.password}
              onChange={handleChange}
            />
          </div>
          {errors.password && <p className="error error-input">{errors.password}</p>}

          <div className="dre-field dre-inline">
            <label className="dre-field-label">Confirm Password</label>
            <input
              type="password"
              name="confirmPassword"
              className="dre-input"
              value={formData.confirmPassword}
              onChange={handleChange}
            />
          </div>
          {errors.confirmPassword && (
            <p className="error error-input">{errors.confirmPassword}</p>
          )}

          <div className="dre-field dre-inline">
            <label className="dre-field-label">English Name</label>
            <input
              type="text"
              name="en_name"
              className="dre-input"
              value={formData.en_name}
              onChange={handleChange}
            />
          </div>

          <div className="dre-field dre-inline">
            <label className="dre-field-label">Marathi Name</label>
            <input
              type="text"
              name="mr_name"
              className="dre-input"
              value={formData.mr_name}
              onChange={handleChange}
            />
          </div>
          {errors.mr_name && <p className="error error-input">{errors.mr_name}</p>}

          <div className="dre-field dre-inline">
            <label className="dre-field-label">Email (optional)</label>
            <input
              type="email"
              name="email"
              className="dre-input"
              value={formData.email}
              onChange={handleChange}
            />
          </div>
          {errors.email && <p className="error error-input">{errors.email}</p>}

          <div className="dre-field dre-inline">
            <label className="dre-field-label">Location (optional)</label>
            <input
              type="text"
              name="location"
              className="dre-input"
              placeholder="e.g. kolhapur — groups dairies for ad targeting"
              value={formData.location}
              onChange={handleChange}
            />
          </div>

          <div className="dre-field dre-inline">
            <label className="dre-field-label">Referred by (optional)</label>
            <input
              type="text"
              name="referred_by"
              className="dre-input"
              placeholder="Who brought this dairy in"
              value={formData.referred_by}
              onChange={handleChange}
            />
          </div>

          <div className="dre-field dre-inline">
            <label className="dre-field-label">Bank Multiplier</label>
            <input
              type="number"
              name="multi"
              min="0"
              className="dre-input"
              value={formData.multi}
              onChange={handleChange}
            />
          </div>
          {errors.multi && <p className="error error-input">{errors.multi}</p>}

          <div className="dre-submit-row">
            <button type="submit" className="dre-submit-btn" disabled={loading}>
              {loading ? "Saving..." : "Create Dairy Admin"}
            </button>
          </div>
        </form>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <DairyAdminsList secretKey={secretKey} />
      </TabPanel>
    </div>
  );
};

export default RegisterDairy;
